import { httpService } from "./httpService";

/**
 * SPEC-002: Driver onboarding + training endpoints.
 * Backend source: fleetmd-backend/src/main/java/.../controller/training/*
 */

/**
 * Org-wide compliance summary — counts per onboarding step, expiring
 * trainings in next 30 days, blocked drivers count.
 *
 * @returns {Promise<{
 *   driverCountByStep: Record<string, number>,
 *   expiringTrainingsNext30Days: number,
 *   blockedDriversCount: number,
 * }>}
 */
export const getComplianceDashboard = async () =>
  httpService({
    url: "/compliance/dashboard",
    options: { method: "GET" },
  });

/**
 * Onboarding state for a specific driver, including outstanding
 * trainings and missing document types.
 *
 * @param {string} driverId
 */
export const getDriverOnboardingStatus = async (driverId) =>
  httpService({
    url: `/drivers/${driverId}/onboarding-status`,
    options: { method: "GET" },
  });

/**
 * Mark a driver as cleared for ride assignment.
 * @param {string} driverId
 */
export const markDriverReadyToDrive = async (driverId) =>
  httpService({
    url: `/drivers/${driverId}/ready-to-drive`,
    options: { method: "POST" },
  });

/**
 * Reject a driver during onboarding (admin only).
 * @param {string} driverId
 * @param {string} reason
 */
export const rejectDriver = async (driverId, reason) =>
  httpService({
    url: `/drivers/${driverId}/reject`,
    options: { method: "POST", data: { reason } },
  });

/**
 * List all training assignments for a specific driver.
 * @param {string} driverId
 */
export const getDriverTraining = async (driverId) =>
  httpService({
    url: `/drivers/${driverId}/training`,
    options: { method: "GET" },
  });

/**
 * Admin assigns a training module to a driver.
 * @param {string} driverId
 * @param {{ trainingModuleId: string, dueBy?: number, notes?: string }} payload
 */
export const assignTrainingToDriver = async (driverId, payload) =>
  httpService({
    url: `/drivers/${driverId}/training/assign`,
    options: { method: "POST", data: payload },
  });

/**
 * Catalog: list active training modules in the tenant catalog.
 */
export const getTrainingModules = async () =>
  httpService({
    url: "/training/modules",
    options: { method: "GET" },
  });

/**
 * List a driver's compliance documents.
 * @param {string} driverId
 */
export const getDriverDocuments = async (driverId) =>
  httpService({
    url: `/drivers/${driverId}/documents`,
    options: { method: "GET" },
  });

/**
 * Admin verifies a driver's document (with optional notes + expiration).
 * @param {string} documentId
 * @param {{ notes?: string, expiresAt?: number }} payload
 */
export const verifyDriverDocument = async (documentId, payload) =>
  httpService({
    url: `/drivers/me/documents/${documentId}/verify`,
    options: { method: "PUT", data: payload || {} },
  });

/**
 * Tenant catalog of policy documents.
 */
export const getPolicies = async () =>
  httpService({
    url: "/policies",
    options: { method: "GET" },
  });

/**
 * Onboarding Kanban — drivers grouped by step.
 * @returns {Promise<Record<string, Array>>}
 */
export const getCompliancePipeline = async () =>
  httpService({
    url: "/compliance/pipeline",
    options: { method: "GET" },
  });

/**
 * Admin: manually move a driver to a different onboarding step (drag-drop).
 * @param {string} driverId
 * @param {{ step: string, note?: string }} payload
 */
export const moveDriverOnboardingStep = async (driverId, payload) =>
  httpService({
    url: `/drivers/${driverId}/onboarding-step`,
    options: { method: "POST", data: payload },
  });

/**
 * Admin: bulk-assign a training module to multiple drivers.
 * @param {{ trainingModuleId: string, driverDetailsIds: string[], dueBy?: number, notes?: string }} payload
 */
export const bulkAssignTraining = async (payload) =>
  httpService({
    url: "/training/bulk-assign",
    options: { method: "POST", data: payload },
  });

/**
 * Admin: waive a training assignment for a driver.
 * @param {string} assignmentId
 * @param {string} reason  required
 */
export const waiveTrainingAssignment = async (assignmentId, reason) =>
  httpService({
    url: `/training/assignments/${assignmentId}/waive`,
    options: { method: "POST", data: { reason } },
  });

/**
 * Admin: waive a required policy for a specific driver.
 * @param {string} driverId
 * @param {string} policyId
 * @param {string} reason  required
 */
export const waiveDriverPolicy = async (driverId, policyId, reason) =>
  httpService({
    url: `/drivers/${driverId}/policies/${policyId}/waive`,
    options: { method: "POST", data: { reason } },
  });

/**
 * Admin: consolidated activity timeline for a driver (newest-first).
 * @param {string} driverId
 */
export const getDriverActivity = async (driverId) =>
  httpService({
    url: `/drivers/${driverId}/activity`,
    options: { method: "GET" },
  });

/**
 * Admin: org-wide newest-first event feed across every driver.
 * @param {{ limit?: number }} opts
 */
export const getOrgActivity = async ({ limit } = {}) =>
  httpService({
    url: `/compliance/activity${limit ? "?limit=" + limit : ""}`,
    options: { method: "GET" },
  });

/**
 * Admin: fleet vehicle compliance — insurance, registration, inspection per vehicle.
 */
export const getVehicleCompliance = async () =>
  httpService({
    url: "/compliance/vehicles",
    options: { method: "GET" },
  });

/**
 * Admin: org-wide drivers master list with per-driver compliance summary.
 */
export const getAllDriversCompliance = async () =>
  httpService({
    url: "/compliance/drivers",
    options: { method: "GET" },
  });

/**
 * Admin: download the All-Drivers compliance list as CSV.
 * Triggers a browser file save via a temporary anchor element.
 */
export const downloadAllDriversComplianceCsv = async () => {
  const { localStorageNameEnum } = await import("models/constants");
  const token = window.sessionStorage.getItem(localStorageNameEnum.AUTH_TOKEN);
  const isDev = process.env.REACT_APP_USER_BRANCH === "dev";
  const baseUrl = isDev
    ? process.env.REACT_APP_API_DEV_URL
    : process.env.REACT_APP_API_URL;
  const resp = await fetch(
    `${baseUrl}/api/v1/compliance/drivers/export-csv`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
  const dispo = resp.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/.exec(dispo);
  const filename = match?.[1] || "drivers-compliance.csv";
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * Admin: all driver documents still awaiting verification (oldest-first).
 */
export const getPendingDocuments = async () =>
  httpService({
    url: "/compliance/pending-documents",
    options: { method: "GET" },
  });

/**
 * Admin: bulk-verify many documents at once.
 * @param {string[]} documentIds
 */
export const bulkVerifyDocuments = async (documentIds) =>
  httpService({
    url: "/drivers/documents/bulk-verify",
    options: { method: "POST", data: { documentIds } },
  });

/**
 * Admin: invite a single driver via email (creates user + emails registration link).
 * @param {{ firstName: string, lastName: string, phone: string, email: string }} payload
 */
export const inviteDriver = async (payload) =>
  httpService({
    url: "/drivers/invite",
    options: { method: "POST", data: payload },
  });

/**
 * Admin: bulk-import drivers from a CSV file.
 * @param {File} file
 * @param {{ sendInvitations?: boolean }} options
 */
export const bulkImportDriversCsv = async (file, { sendInvitations = false } = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  return httpService({
    url: `/drivers/bulk-import?sendInvitations=${sendInvitations}`,
    options: {
      method: "POST",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    },
  });
};

/**
 * Admin: renewal calendar — every training + policy expiring in a date window.
 * Defaults: last 30 days .. next 90 days.
 * @param {{ fromMs?: number, toMs?: number }} window
 */
export const getRenewalCalendar = async ({ fromMs, toMs } = {}) => {
  const q = [];
  if (fromMs) q.push(`fromMs=${fromMs}`);
  if (toMs) q.push(`toMs=${toMs}`);
  return httpService({
    url: `/compliance/renewals${q.length ? "?" + q.join("&") : ""}`,
    options: { method: "GET" },
  });
};

/**
 * Admin: download the compliance audit ZIP for a driver.
 * Triggers a browser file save via a temporary anchor element.
 * @param {string} driverId
 */
export const downloadComplianceExport = async (driverId) => {
  const { localStorageNameEnum } = await import("models/constants");
  const token = window.sessionStorage.getItem(localStorageNameEnum.AUTH_TOKEN);
  const isDev = process.env.REACT_APP_USER_BRANCH === "dev";
  const baseUrl = isDev
    ? process.env.REACT_APP_API_DEV_URL
    : process.env.REACT_APP_API_URL;

  const resp = await fetch(
    `${baseUrl}/api/v1/drivers/${driverId}/compliance-export`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!resp.ok) {
    throw new Error(`Export failed (${resp.status})`);
  }
  // Pull filename from Content-Disposition
  const dispo = resp.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/.exec(dispo);
  const filename = match?.[1] || `compliance-${driverId}.zip`;

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * Per-driver policy acknowledgment status (PENDING / SIGNED / EXPIRED / REVOKED).
 * @param {string} driverId
 */
export const getDriverPolicies = async (driverId) =>
  httpService({
    url: `/drivers/${driverId}/policies`,
    options: { method: "GET" },
  });
